import React from 'react'

const sailingEngland = {
    id: 'sailing-england',
    type: 'post',
    countryId: 'uk',
    title: 'Learning How to Sail in England',
    longDate: 'July 17, 2018',
    month: 'July',
    date: '17',
    coverImage: 'https://plivesey.github.io/world-of-wander-images/posts/uk/UK.jpg',
    coverImagePosition: 'top',
    content: [
        {
            text: (
                <div>When we lived in Mountain View, Peter found a tiny man-made lake near the salt flats and signed up for ten rental sessions. He had sailed as a child; his dad is a yachtmaster (a technical designation and also a cool title) and Peter had loved the sailing vacations the family took. I'm not sure we used all ten sessions, because the tiny lake quickly became tiresome when you needed to change directions constantly going back and forth.</div>
            )
        },
        {
            text: (
                <div>The only other time I'd been on a boat was for a friend's 30th birthday on a yacht, where we were asked to come in dress code "fabulous" and did as much drinking as looking at the water. I had thought that was the only boating experience I needed to have going forward. When Peter's parents suggested renting a yacht for a week at the Isle of Wight, I was on board – picturing smooth sailing and lots of Pimm's. </div>
            )
        },
        {
            text: (
                <div>An hour in, as the boat pitched at a 45 degree angle (ok fine, probably 20 degrees) and I cowered trying not to be sick, I wasn't so sure. Luckily the week turned out to be fantastic and we've already planned our next trip. We were also stupidly lucky with the sunny weather. Sailing in England is supposed to be a dreary, wet occasion, and we had even bought massive waterproof overalls for it. </div>
            )
        },
        {
            text: (
                <div>Here's what I learned about sailing!</div>
            )
        },
        {
            header: '1. Boats "heel" at crazy angles'
        },
        {
            text: (
                <div>I was terrified the first day, expecting (not sure why?) that the boat would stay pretty flat as we sailed. The tiny boat we'd tried in Mountain View had heeled a bit, but not at a sharp angle. A bigger boat meant you could be much higher out the water.</div>
            )
        },
        {
            text: (
                <div>Boats heel because of the mechanics of the sails: you position the sails such that the wind blows into them, at an angle to the direction your boat is pointed. The strength of the wind pushes the boat over while also powering it to move forward.</div>
            )
        },
        {
            text: (
                <div>But, it was super fun once I got over my fear! If you're a passenger, the best position is to sit on the side that's up out of the water. You're constantly switching sides as you tack.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Heeling.jpg',
            detailText: (
                <div>This boat isn't falling over, it's just sailing forward. Photo source: <a href="https://healingonthebay.com/hob-charters/">Healing on the Bay</a>.</div>
            )
        },
        {
            header: '2. "Tacking" changes how your boat approaches the wind'
        },
        {
            text: (
                <div>Sailing is pretty inefficient. You don't sail in a straight line; you're constantly zigzagging across the water.</div>
            )
        },
        {
            text: (
                <div>Tacking happens when you turn the boat, causing the wind to hit the other side of the sail. This moves the sail to the other side of the boat, and the boat switches the direction it's leaning. If you're headed at a good (30-50 degree angle) to the direction the wind is blowing, it's easy. You use the steering wheel and keep the wind on the same side of the sail. But if you're headed upwind or downwind, you'll have a problem. The wind has to catch the sails from the side, so it can't fill the sails if it's hitting the boat from the front or back. So, you zigzag across the line of wind to make sure the wind is filling the sails and propelling the boat forward.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Tacking.jpg',
            detailText:
                (
                    <div>Photo source: <a href="https://www.slideshare.net/emilygoodstein/sailing-101">Sailing 101 Slideshare</a> by Peter Goldstein.</div>
                )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Manatthehelm.jpg',
            detailText: "Get yourself a yachtmaster who actually knows this stuff; bonus if they're your father-in-law."
        },
        {
            header: '3. Motors are necessary'
        },
        {
            text: (
                <div>Because sailing can be so inefficient (see above), sometimes you get stuck with no wind and have to turn on the motor to keep moving. Most sailboats are equipped with a motor because it's difficult to put down an anchor without one, and basically impossible to get in and out of a marina. I suppose this one is obvious in retrospect, but I it's not something I'd thought about at all before learning how to put down an anchor. I have a renewed respect for Pirates of the Caribbean-esque boats now knowing how difficult maneuvering can be.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/SailingAnchor.jpg',
            detailText: 'Motoring into bay with the sails down to put down an anchor (at the front of the boat, right hand side of the picture.)'
        },
        {
            header: '4. Tides are critical for timing'
        },
        {
            text: (
                <div>The mechanics of how to make a sailboat move in a given direction are easy enough to understand and practice, but that's a small portion of mastering the seas. Navigation can be immensely difficult, and this is another point on which I'm in awe of the Pirates of the Caribbean. To get from point A to B is not just looking at the map and making a straight line. You have to time coming in and out of inlets or bays with the direction the tides are going. We didn't plan our entire week's route beforehand so we could be flexible each day, but that meant close to half an hour of Hilary and Alastair poring over tidal maps and estimating how many knots per hour we could travel.</div>
            )
        },
        {
            text: (
                <div>Fun fact: 'knots' are the nautical equipment of miles, a standard of measurement. But it's not an accidentally bad way to shorten 'nautical' (nauts… get it?). It actually comes from when there were standard length ropes with knots at certain intervals, and boats would time how long it took to get from one knot to the next to know how fast they were going.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/SailingMap.jpg',
            detailText: 'One of the complicated maps we consulted frequently.'
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/RacingBoats.jpg',
            detailText: "These kids learning how to race probably aren't doing anything related to tides or navigation in this photo, but I love it anyway."
        },
        {
            header: '5. Sunsets are truly stunning from the water'
        },
        {
            text: (
                <div>I'm not really an ocean person, but man – it's truly gorgeous to stare across the open water as the sun sets. Here's some photographic evidence.</div>
            )
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Sunset1.jpg',
        },
        {
            images: ['https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Sunset2.jpg', 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Sunset3.jpg']
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Sunset4.jpg',
        },
        {
            image: 'https://plivesey.github.io/world-of-wander-images/posts/uk/sailing-england/Sunset5.jpg',
        },
    ]
}

export default sailingEngland


// WEBPACK FOOTER //
// ./src/Posts/Content/Sailing.js